/**
 * Iter 96 — always-reachable reservation search (spec §9). A search icon
 * button that opens a bottom sheet; matches reservation number, customer
 * name/phone/email, vehicle make/model/plate and (admin) vendor name.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import BookingStatusBadge from "@/components/ops/BookingStatusBadge";

function matches(b, q) {
  const hay = [
    b.id, (b.id || "").slice(0, 8),
    b.customer?.name, b.customer?.email, b.customer?.phone, b.customer?.whatsapp,
    b.car?.make, b.car?.model, `${b.car?.make || ""} ${b.car?.model || ""}`,
    b.car?.license_plate, b.car?.plate_number,
    b.car?.vendor?.business_name, b.car?.vendor?.name,
    b.pickup_location,
  ];
  return hay.some((v) => String(v || "").toLowerCase().includes(q));
}

export default function MobileBookingSearch({ bookings = [] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const results = useMemo(() => (q.length < 2 ? [] : bookings.filter((b) => matches(b, q)).slice(0, 20)), [bookings, q]);

  return (
    <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="h-10 w-10 rounded-full border border-border bg-white flex items-center justify-center text-neutral-600 hover:border-neutral-400 transition shrink-0"
          data-testid="ops-search-trigger"
          aria-label="Search reservations"
        >
          <Search className="h-4.5 w-4.5" />
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="rounded-t-3xl h-[80vh] flex flex-col" data-testid="ops-search-sheet">
        <SheetHeader className="mb-2">
          <SheetTitle className="text-left">Find a reservation</SheetTitle>
        </SheetHeader>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Reservation #, customer, phone, email, plate, vehicle…"
          className="rounded-xl h-11"
          autoFocus
          data-testid="ops-search-input"
        />
        <div className="flex-1 overflow-y-auto mt-3 space-y-2 pb-4">
          {q.length >= 2 && results.length === 0 && (
            <div className="text-sm text-neutral-500 text-center py-8" data-testid="ops-search-empty">No reservations match "{query}".</div>
          )}
          {results.map((b) => (
            <Link
              key={b.id}
              to={`/agreements/${b.id}`}
              onClick={() => setOpen(false)}
              className="block rounded-2xl border border-border bg-white p-3.5 hover:border-blue-300 transition"
              data-testid={`ops-search-result-${b.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[11px] font-bold text-neutral-500">#{(b.id || "").slice(0, 8).toUpperCase()}</span>
                <BookingStatusBadge booking={b} />
              </div>
              <div className="flex items-start gap-3 mt-1">
                {b.car?.images?.[0] && (
                  <img src={b.car.images[0]} alt={`${b.car?.make || ""} ${b.car?.model || ""}`.trim()} className="h-12 w-16 rounded-lg object-cover shrink-0 ring-1 ring-border" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid={`ops-search-car-image-${b.id}`} />
                )}
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{b.car?.make} {b.car?.model}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {b.customer?.name ? `${b.customer.name} · ` : ""}{String(b.start_date || "").slice(0, 10)} → {String(b.end_date || "").slice(0, 10)}
                    {b.pickup_location ? ` · ${b.pickup_location}` : ""}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
